import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Debt Payoff Calculator (Avalanche) + Extra payments + Fixed-total option
export default function DebtPayoffCalc() {
  const [debts, setDebts] = useState([
    { name: 'Auto loan', balance: 25000, min: 519, rate: 4.9 },
    { name: 'Home mortgage', balance: 250000, min: 1800, rate: 4 },
    { name: 'Credit card 1', balance: 6000, min: 150, rate: 18.99 },
    { name: 'Credit card 2', balance: 3000, min: 60, rate: 16.99 },
  ]);
  const [extraM, setExtraM] = useState(100);
  const [extraY, setExtraY] = useState(0);
  const [oneTime, setOneTime] = useState(0);
  const [oneTimeMonth, setOneTimeMonth] = useState(5);
  const [fixedTotal, setFixedTotal] = useState('yes');

  const update = (i, patch) => setDebts(prev => prev.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const addDebt = () => setDebts(prev => [...prev, { name: `Debt ${prev.length+1}`, balance: 0, min: 0, rate: 0 }]);

  const c = useMemo(() => {
    const active = debts.filter(x => x.balance > 0).map((x, idx) => ({ ...x, idx, origBalance: x.balance, monthsToPayoff: 0, totalInt: 0, totalPaid: 0, schedule: [] }));
    const totalMin = active.reduce((a,b)=>a+b.min, 0);
    const lastPay = active.map(() => null);
    let month = 0; let totalInt = 0; let totalPaid = 0;
    while (active.some(x => x.balance > 0.01) && month < 1200) {
      month++;
      let budget;
      if (fixedTotal === 'yes') budget = totalMin + extraM;
      else budget = active.filter(x => x.balance > 0).reduce((a,b)=>a+b.min, 0) + extraM;
      if (month % 12 === 0) budget += extraY;
      if (month === oneTimeMonth) budget += oneTime;
      const sortedIdx = [...active.keys()].filter(i => active[i].balance > 0).sort((a,b) => active[b].rate - active[a].rate);
      let allocated = 0;
      for (const i of sortedIdx) {
        const int = active[i].balance * active[i].rate/100/12;
        totalInt += int;
        active[i].totalInt += int;
        active[i].balance += int;
        allocated += active[i].min;
      }
      let extra = Math.max(0, budget - allocated);
      for (const i of sortedIdx) {
        let pay = active[i].min + (i === sortedIdx[0] ? extra : 0);
        if (pay > active[i].balance) { extra = pay - active[i].balance; pay = active[i].balance; }
        else extra = 0;
        active[i].balance = Math.max(0, active[i].balance - pay);
        active[i].totalPaid += pay;
        totalPaid += pay;
        // Record payment segment
        const key = pay.toFixed(2);
        if (lastPay[i] !== key) {
          active[i].schedule.push({ startMonth: month, endMonth: month, amount: pay });
          lastPay[i] = key;
        } else {
          active[i].schedule[active[i].schedule.length - 1].endMonth = month;
        }
        if (active[i].balance <= 0 && active[i].monthsToPayoff === 0) active[i].monthsToPayoff = month;
      }
    }
    return { months: month, totalInt, totalPaid, breakdown: active };
  }, [debts, extraM, extraY, oneTime, oneTimeMonth, fixedTotal]);

  const fmtSchedule = (d) => {
    if (!d.schedule || d.schedule.length === 0) return '—';
    return d.schedule.map((seg, i) => {
      const last = i === d.schedule.length - 1;
      if (seg.startMonth === seg.endMonth) return last ? `Pay $${seg.amount.toFixed(2)} at month ${seg.endMonth} to payoff.` : `Then pay $${seg.amount.toFixed(2)} at month ${seg.endMonth}.`;
      return `${i === 0 ? 'Pay' : 'Then pay'} $${seg.amount.toFixed(2)} until month ${seg.endMonth - (last ? 0 : 0)}.`;
    }).join(' ');
  };

  return (
    <CalcShell look="cnet" title="Debt Payoff Calculator" subtitle="Debt Avalanche payoff planner with extra monthly/yearly/one-time payments."
      about={<><p>Prioritize highest-interest debts first (Avalanche) or lowest-balance first (Snowball). This tool uses <b>Avalanche</b> plus configurable extra payments to model your fastest payoff.</p></>}
    >
      <table className="w-full text-sm">
        <thead className="bg-slate-100"><tr><th className="px-2 py-2 text-left">Debt name</th><th className="px-2 py-2 text-right">Remaining balance</th><th className="px-2 py-2 text-right">Monthly or min. payment</th><th className="px-2 py-2 text-right">Interest rate</th></tr></thead>
        <tbody>
          {debts.map((d, i) => (
            <tr key={i} className="border-b">
              <td className="px-2 py-2"><input type="text" value={d.name} onChange={e=>update(i, { name: e.target.value })} className="!py-1"/></td>
              <td className="px-2 py-2"><div className="flex gap-1"><span>$</span><input type="number" value={d.balance} onChange={e=>update(i, { balance: +e.target.value||0 })} className="!py-1 flex-1"/></div></td>
              <td className="px-2 py-2"><div className="flex gap-1"><span>$</span><input type="number" value={d.min} onChange={e=>update(i, { min: +e.target.value||0 })} className="!py-1 flex-1"/></div></td>
              <td className="px-2 py-2"><div className="flex gap-1"><input type="number" step="0.01" value={d.rate} onChange={e=>update(i, { rate: +e.target.value||0 })} className="!py-1 flex-1"/><span>%</span></div></td>
            </tr>
          ))}
        </tbody>
      </table>
      {debts.length < 10 && <button onClick={addDebt} className="text-blue-700 text-sm font-semibold mt-2">+ Show more input fields</button>}

      <h3 className="font-semibold text-slate-900 mt-6 mb-2">Extra payments</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="field"><label>Monthly</label><div className="flex gap-1"><span>$</span><input type="number" value={extraM} onChange={e=>setExtraM(+e.target.value||0)} className="flex-1"/><span>per month</span></div></div>
        <div className="field"><label>Yearly</label><div className="flex gap-1"><span>$</span><input type="number" value={extraY} onChange={e=>setExtraY(+e.target.value||0)} className="flex-1"/><span>per year</span></div></div>
        <div className="field"><label>One-time payment</label><div className="flex gap-1"><span>$</span><input type="number" value={oneTime} onChange={e=>setOneTime(+e.target.value||0)} className="flex-1"/><span>made during the</span><input type="number" value={oneTimeMonth} onChange={e=>setOneTimeMonth(+e.target.value||0)} className="w-14"/><span>th month</span></div></div>
      </div>

      <div className="mt-4">
        <label className="font-semibold text-sm">Fixed total amount towards monthly payment?</label>
        <div className="flex gap-4 mt-1 text-sm"><label><input type="radio" checked={fixedTotal==='yes'} onChange={()=>setFixedTotal('yes')}/> Yes</label><label><input type="radio" checked={fixedTotal==='no'} onChange={()=>setFixedTotal('no')}/> No</label></div>
        <p className="text-xs text-slate-600 mt-2 max-w-2xl">{fixedTotal === 'yes' ? 'When a debt is paid off, its former payment is redistributed to remaining debts. The total budget stays constant until all debts are cleared.' : 'When a debt is paid off, its former payment is NOT redistributed. The total budget decreases as debts are cleared.'}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className="result-panel"><div className="result-title">Payoff time</div><div className="result-value" data-testid="dp-time">{`${Math.floor(c.months/12)} yr ${c.months%12} mo`}</div></div>
        <div className="result-panel"><div className="result-title">Total interest paid</div><div className="result-value">{fmt(c.totalInt)}</div></div>
      </div>

      {/* Per-debt breakdown table — matches Calculator.net Debt Payoff reference */}
      <div className="mt-6">
        <p className="text-sm text-slate-700 mb-3">You can pay off your debts in <b className="text-green-700">{c.months} months ({Math.floor(c.months/12)} years and {c.months%12} months)</b> by making fixed payments of <b>{fmt((totalMin => totalMin + extraM)(debts.reduce((a,b)=>a+b.min,0)))}</b> every month{extraM > 0 && <>, of which <b>{fmt(extraM)}</b> is the extra monthly payment</>}. You will need to pay a total of <b>{fmt(c.totalPaid)}</b>, of which the total interest is <b>{fmt(c.totalInt)}</b>.</p>
        <p className="text-sm text-slate-700 mb-3">The most financially feasible method to pay off debts is to start by paying off the highest interest debts first while paying the monthly or minimum payments for the other debts. The following is the payment schedule.</p>
        <div className="overflow-auto border border-slate-200 rounded">
          <table className="w-full text-sm border-collapse" data-testid="dp-details-table">
            <thead className="bg-blue-800 text-white">
              <tr>
                <th className="px-3 py-2 text-left">Debt</th>
                <th className="px-3 py-2 text-left">Payoff length</th>
                <th className="px-3 py-2 text-right">Total interest</th>
                <th className="px-3 py-2 text-right">Total payments</th>
                <th className="px-3 py-2 text-left">Payment schedule</th>
              </tr>
            </thead>
            <tbody>
              {c.breakdown.slice().sort((a,b)=>a.monthsToPayoff - b.monthsToPayoff).map((d, i) => (
                <tr key={i} className={`border-t border-slate-200 ${i%2?'bg-slate-50':''}`}>
                  <td className="px-3 py-2 whitespace-nowrap">#{d.idx+1}: {d.name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{d.monthsToPayoff} months ({Math.floor(d.monthsToPayoff/12)} year{Math.floor(d.monthsToPayoff/12)!==1?'s':''} and {d.monthsToPayoff%12} months)</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(d.totalInt)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(d.totalPaid)}</td>
                  <td className="px-3 py-2 text-xs leading-relaxed">{fmtSchedule(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Description panel */}
      <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-2">
        <p><b>Debt Avalanche</b> — this calculator uses the Avalanche method, which pays minimums on all debts while directing every extra dollar to the highest-interest-rate debt first. Once that debt is cleared, its former payment "cascades" onto the next highest-rate debt.</p>
        <p><b>Debt Snowball</b> — a psychological alternative that targets the smallest balance first for quick wins. This calculator uses Avalanche because it mathematically minimizes total interest.</p>
        <p><b>Fixed total payment</b> — when a debt is paid off, its former payment is redistributed to remaining debts, keeping your total monthly outlay constant. This accelerates payoff significantly compared to letting the monthly total drop.</p>
      </div>
    </CalcShell>
  );
}
